import React, { useEffect, useState } from 'react';

import { DateTime } from 'luxon';

import styled from '@emotion/styled';

import {
  AvailabilityFragment,
  MultidayAvailabilityFragment,
} from '@graphql/platform';

import type { SharedCheckoutDateScheduled } from '@root/components/checkout/data';
import { useTrack } from '@root/initializers/wt';

import { FROM_ISO_OPTIONS } from '@shared/calendar/calendar_utils';

import { formatCurrency } from '@utils/formatters';

import {
  availabilityWindowMatches,
  isFlexibleAvailability,
  isScheduledAvailability,
} from '../availability';
import {
  CardPadded,
  HelpText,
  Question,
  SelectRow,
  SelectStack,
} from '../shared';
import { CHALLENGER as T } from '../tokens';

type Availability = AvailabilityFragment | MultidayAvailabilityFragment;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[24]};
  width: 100%;
`;

const ScheduledBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[8]};
  width: 100%;
`;

/**
 * Figma 898:21873 — up to three equal chips in one row (gap 16).
 * Cap each chip at 1/3 width so a single window doesn’t stretch full-bleed
 * (classic used maxWidth 224px for the same reason).
 */
const SlotRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: ${T.space[16]};
  width: 100%;
`;

/** Three columns with 16px gaps → each chip ≤ (100% − 32px) / 3. */
const SLOT_MAX = 'calc((100% - 32px) / 3)';

const SlotButton = styled.button<{ selected?: boolean }>`
  appearance: none;
  cursor: pointer;
  flex: 1 1 ${SLOT_MAX};
  max-width: ${SLOT_MAX};
  min-width: min(140px, 100%);
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  padding: ${T.space[12]} ${T.space[16]};
  border-radius: ${T.radius.md};
  border: 1px solid
    ${({ selected }) =>
      selected ? T.color.borderBrand : T.color.borderDefault};
  background: ${({ selected }) =>
    selected ? T.color.bgSelected : T.color.bgPrimary};
  color: ${T.color.textPrimary};
  text-align: left;
  box-sizing: border-box;

  &:hover {
    border-color: ${T.color.borderBrand};
  }
`;

type Props = {
  selectedAvailability?: SharedCheckoutDateScheduled;
  availabilities: ReadonlyArray<Availability>;
  /** Fee from ZIP-level availability before a calendar day is chosen. */
  scheduledFeePreview?: number;
  /** True once the customer has picked a calendar day. */
  dateSelected?: boolean;
  /**
   * Set a window, or pass `undefined` to clear the arrival choice (no auto
   * progression — customer must pick Flexible or a Scheduled chip).
   */
  onChange(availability: Availability | undefined, autoScroll: boolean): void;
};

/** Figma: `7AM - 10AM` (no minutes when :00, no space before AM/PM). */
const formatSlotClock = (value: DateTime): string => {
  const meridiem = value.toFormat('a').replace(/\s/g, '').toUpperCase();
  if (value.minute === 0) {
    return `${value.toFormat('h')}${meridiem}`;
  }
  return `${value.toFormat('h:mm')}${meridiem}`;
};

const formatSlotRange = (from: DateTime, till: DateTime): string =>
  `${formatSlotClock(from)} - ${formatSlotClock(till)}`;

/**
 * Figma Desktop 03/04 / node 707:10595 — Flexible + Scheduled + time chips.
 * Do not auto-select Flexible (or any window) when a calendar day is chosen.
 */
export const ChallengerArrivalOptions: React.FC<Props> = ({
  selectedAvailability,
  availabilities,
  scheduledFeePreview,
  dateSelected = false,
  onChange,
}) => {
  /**
   * Pending “Scheduled” UI before a time chip is chosen. Cleared when the
   * calendar day changes (component remounts via key) or Flexible is selected.
   * Do not clear when `selectedAvailability` becomes undefined — that happens
   * on the first Scheduled click after Flexible (clearing Flexible).
   */
  const [scheduledPending, setScheduledPending] = useState(
    () =>
      !!(selectedAvailability && isScheduledAvailability(selectedAvailability)),
  );
  const track = useTrack({ action: 'click' });

  useEffect(() => {
    if (!dateSelected) {
      setScheduledPending(false);
    }
  }, [dateSelected]);

  const flexibleWindow = availabilities.find(isFlexibleAvailability);
  const scheduledWindows = availabilities.filter(isScheduledAvailability);
  const noPickupAvailability = !dateSelected || availabilities.length === 0;

  const selectedFlexible = !!(
    !noPickupAvailability &&
    !scheduledPending &&
    selectedAvailability &&
    isFlexibleAvailability(selectedAvailability)
  );
  const selectedScheduled = !!(
    !noPickupAvailability &&
    (scheduledPending ||
      (selectedAvailability && isScheduledAvailability(selectedAvailability)))
  );

  const scheduledUnavailable =
    dateSelected && availabilities.length > 0 && scheduledWindows.length === 0;
  const flexibleDisabled = noPickupAvailability || !flexibleWindow;
  const scheduledDisabled =
    noPickupAvailability ||
    scheduledUnavailable ||
    scheduledWindows.length === 0;

  const scheduledFee = scheduledUnavailable
    ? undefined
    : scheduledWindows.length > 0
      ? (scheduledWindows[0].feeAmount?.value ?? undefined)
      : scheduledFeePreview;

  /** Funnel-Feedback 6:699 — available copy; keep unavailable messaging for empty days. */
  const scheduledDescription = scheduledUnavailable
    ? 'Not available on this date. Choose a different pickup date to use this option.'
    : 'Choose the arrival window that works for your schedule. This is the ideal option if your building requires reservations or has restrictions.';

  /** Chips once Scheduled is chosen (pending or a slot already selected). */
  const showTimeSlots = selectedScheduled && scheduledWindows.length > 0;

  return (
    <CardPadded>
      <Options>
        <Header>
          <Question>What arrival window works for you?</Question>
          <HelpText>
            Our team will arrive anytime during the arrival window. Your pickup
            may continue beyond this window.
          </HelpText>
        </Header>
        <SelectStack>
          <SelectRow
            selected={selectedFlexible}
            disabled={flexibleDisabled}
            onClick={() => {
              if (flexibleDisabled || !flexibleWindow) return;
              track({
                objectName: 'arrival_window',
                objectType: 'button',
                label: 'Arrival between 7am - 3pm',
                value: flexibleWindow.formatted,
              });
              setScheduledPending(false);
              onChange(flexibleWindow, false);
            }}
            title="Arrival between 7am - 3pm"
            description="We will notify you of your arrival window the day before."
            price="Included"
          />
          <ScheduledBlock>
            <SelectRow
              selected={selectedScheduled}
              disabled={scheduledDisabled}
              onClick={() => {
                if (scheduledDisabled) {
                  return;
                }
                track({
                  objectName: 'arrival_window',
                  objectType: 'button',
                  label: 'Arrival during a specific window',
                });
                // Expand chips only — do not auto-pick the first window.
                setScheduledPending(true);
                if (
                  selectedAvailability &&
                  isFlexibleAvailability(selectedAvailability)
                ) {
                  onChange(undefined, false);
                }
              }}
              title="Arrival during a specific window"
              description={scheduledDescription}
              price={
                scheduledFee != null
                  ? formatCurrency(scheduledFee, { maximumFractionDigits: 0 })
                  : '—'
              }
            />
            {showTimeSlots && (
              <SlotRow>
                {scheduledWindows.map((availability) => {
                  const selected =
                    !!selectedAvailability &&
                    availabilityWindowMatches(
                      availability,
                      selectedAvailability,
                    );
                  const from = DateTime.fromISO(
                    availability.fromTime,
                    FROM_ISO_OPTIONS,
                  );
                  const till = DateTime.fromISO(
                    availability.tillTime,
                    FROM_ISO_OPTIONS,
                  );
                  return (
                    <SlotButton
                      key={`${availability.fromTime}-${availability.tillTime}`}
                      type="button"
                      selected={selected}
                      onClick={() => {
                        track({
                          objectName: 'arrival_window',
                          objectType: 'button',
                          label: formatSlotRange(from, till),
                          value: availability.formatted,
                        });
                        setScheduledPending(true);
                        onChange(availability, false);
                      }}
                    >
                      {formatSlotRange(from, till)}
                    </SlotButton>
                  );
                })}
              </SlotRow>
            )}
          </ScheduledBlock>
        </SelectStack>
      </Options>
    </CardPadded>
  );
};
